import { MouseEventHandler, useState } from 'react';
import { addMonths } from 'date-fns';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { getMinimumEntryDate } from '../domain/utils/getMinimumEntryDate';
import { EntryTimeSelect } from './time/EntryTimeSelect';
import { ExitTimeSelect } from './time/ExitTimeSelect';
import { ArrowForwardIcon, Css, Responsive } from '../../../ui';
import {
	MaybeDate,
	MaybeDateRange,
} from '../../../components/common/date-picker/MaybeDate';
import { MaybeTimeOption } from '../../../components/common/time-select/TimeOption';
import { getParkingRerouteUrl } from '../domain/utils/getParkingRerouteUrl';
import Button from '../../../components/common/button/Button';
import TextArrowButton from '../../../components/common/button/TextArrowButton';
import { PageRoutes } from '../../app/pages/domain/constants/PageRoutes';
import { fadeIn } from '../../../styles/animation/fadeIn';
import { useNavigate } from '../../../navigation/useNavigate';
import { AirportCodesWithParkingBooking } from '../domain/models/AirportParkingBookingRoute';
import { AirportCodes } from '../../../types/airports';

const DatePicker = dynamic(
	() =>
		import('../../../components/common/date-picker/DatePicker').then(
			(mod) => mod.DatePicker,
		),
	{ ssr: false },
);

interface IProps {
	isLanding?: boolean;
	airportCode: AirportCodesWithParkingBooking;
}

const StyledParkingWidgetForm = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: ${Css.pxToRem(16)};

	${fadeIn}
`;

const StyledFormRow = styled.div<{ alignEnd?: boolean }>`
	display: flex;
	flex-direction: row;
	width: 100%;
	justify-content: flex-end;
	gap: ${Css.pxToRem(16)};

	&:nth-of-type(2) {
		justify-content: ${({ alignEnd }) =>
			alignEnd ? 'flex-end' : 'space-between'};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
	}
`;

const StyledSubmitButton = styled(Button)`
	max-width: 240px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-grow: 1;
		justify-content: center;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		max-width: 100%;
	}
`;

export const ParkingBookingWidgetForm = ({
	isLanding = false,
	airportCode,
}: IProps) => {
	const navigate = useNavigate();
	const [startDate, setStartDate] = useState<MaybeDate>(null);
	const [endDate, setEndDate] = useState<MaybeDate | null>(null);
	const [maxDate, setMaxDate] = useState<MaybeDate | null>(null);
	const [startTime, setStartTime] = useState<MaybeTimeOption | null>(null);
	const [endTime, setEndTime] = useState<MaybeTimeOption | null>(null);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');

	const requiredMinEntryDate = getMinimumEntryDate(
		// For IAH we don't have the 2 hour parking restriction, so the minimum entry date - current time
		// For HOU, it's 2 hours and 1 second from the current time
		airportCode === AirportCodes.IAH ? 0 : 60 * 60 * 2 + 1,
	);

	const parkingAvailabilitySlugMap: {
		[key in AirportCodes]?: PageRoutes;
	} = {
		[AirportCodes.IAH]: PageRoutes.IAHParkingAvailability,
		[AirportCodes.HOU]: PageRoutes.HOUParkingAvailability,
	};

	const onMoreInformation = () => {
		navigate(`${parkingAvailabilitySlugMap[airportCode]}`);
	};

	const onChangeDates = ([start, end]: MaybeDateRange) => {
		setStartDate(start);
		setEndDate(end);

		if (start) {
			let newMaxDate = new Date(start);
			newMaxDate = addMonths(newMaxDate, 12);
			setMaxDate(newMaxDate);
		}
	};

	const onSubmit: MouseEventHandler = (e) => {
		e.preventDefault();
		setIsSubmitted(true);
		if (!startDate || !endDate || !startTime || !endTime) {
			return;
		}

		const rerouteUrl = getParkingRerouteUrl(
			airportCode,
			startDate,
			endDate,
			startTime,
			endTime,
		);

		window.location.href = rerouteUrl;
	};

	return (
		<StyledParkingWidgetForm>
			<StyledFormRow>
				<DatePicker
					startDate={startDate}
					endDate={endDate}
					onChange={onChangeDates}
					invalid={(!startDate || !endDate) && isSubmitted}
					aria-label="Select your parking period"
					placeholderText="Select your parking period"
					maxDate={maxDate}
					minDate={requiredMinEntryDate}
				/>
				<EntryTimeSelect
					onChange={setStartTime}
					selectedItem={startTime}
					entryDate={startDate}
					minimumEntryDate={requiredMinEntryDate}
					ariaLabel="Entrance time"
					placeholder="Entrance time*"
					invalid={!startTime && isSubmitted}
				/>
				<ExitTimeSelect
					entryDate={startDate}
					exitDate={endDate}
					entryTime={startTime}
					onChange={setEndTime}
					selectedItem={endTime}
					ariaLabel="Exit time"
					placeholder="Exit time*"
					invalid={!endTime && isSubmitted}
				/>
			</StyledFormRow>
			<StyledFormRow alignEnd={!isLanding}>
				{isLanding && !isMobileXL && (
					<TextArrowButton onClick={onMoreInformation}>
						More information about parking
					</TextArrowButton>
				)}
				<StyledSubmitButton
					onClick={onSubmit}
					buttonType="primary"
					type="button"
					icon={<ArrowForwardIcon aria-hidden />}
				>
					Book your parking
				</StyledSubmitButton>
				{isLanding && isMobileXL && (
					<TextArrowButton onClick={onMoreInformation}>
						More information about parking
					</TextArrowButton>
				)}
			</StyledFormRow>
		</StyledParkingWidgetForm>
	);
};
